import { FC, useState, useRef } from 'react';
import { useForm, usePage } from '@inertiajs/react';

import { useInteractiveCallback } from '@/hook/useInteractiveCallback';
import { AppModalProps } from '@/types/modal.type';

import { InputLabel } from './InputLabel';
import TextInput, { TextInputHandle } from './TextInput';
import { InputError } from './InputError';
import { ModalView } from './ModalView';
import { LogoSvg } from './Icons/LogoSvg';
import { ExclamationSvg } from './Icons/ExclamationSvg';
import { Button } from './Button';

type ConfirmPasswordModalProps = AppModalProps<{
    title?: string;
    content?: string;
    username?: string;
    onClose: () => void;
    onConfirm: () => void;
}>;

export const ConfirmPasswordModal: FC<ConfirmPasswordModalProps> = ({
    onClose,
    payload,
}) => {
    const { title, content, username, onConfirm } = payload;

    const [processing, setProcessing] = useState(false);

    const {
        data,
        processing: formProcessing,
        errors,
        setData,
        post,
        reset,
    } = useForm({ password: '' });

    const inputRef = useRef<TextInputHandle>(null);

    const focusInput = () => {
        inputRef.current?.focus();
    };

    const confirmPassword = useInteractiveCallback(
        (event?: React.FormEvent<HTMLFormElement>) => {
            event?.preventDefault();

            setProcessing(true);

            post(route('password.confirm.json'), {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    onConfirm();
                    setData('password', '');
                    reset();
                    onClose();
                },
                onFinish: () => {
                    setProcessing(false);
                },
            });
        },
    );

    const handleClose = () => {
        setData('password', '');
        reset();
        onClose();
    };

    return (
        <ModalView
            maxWidth="sm"
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center"
            onAnimationStart={focusInput}
            onClose={handleClose}>
            <div className="flex flex-row items-center justify-center">
                <LogoSvg className="mb-8 -mr-1.5" />
                <ExclamationSvg className="[&_path]:fill-brand-teal" />
            </div>
            <h2 className="text-heading-three font-bold text-brand-ocean mb-2">
                {title || 'Security Check'}
            </h2>
            <p className="mt-1 text-body-text-15 text-black text-left w-full mb-3">
                {content || 'We need to confirm it’s really you.'}
            </p>
            <form onSubmit={confirmPassword} className="w-full">
                <div>
                    {username ? (
                        <input
                            defaultValue={username}
                            type="text"
                            name="email"
                            autoComplete="username email"
                            className="hidden"
                        />
                    ) : null}
                    <InputLabel
                        htmlFor="confirm-password"
                        value="Password"
                        className="text-heading-five !font-bold text-brand-ocean"
                    />
                    <TextInput
                        ref={inputRef}
                        required
                        id="confirm-password"
                        type="password"
                        autoComplete="current-password"
                        className="mt-1 block w-full"
                        value={data.password}
                        onChange={(event) =>
                            setData('password', event.target.value)
                        }
                    />
                    <InputError message={errors.password} className="mt-2" />
                </div>
                <div className="w-full mt-6">
                    <Button
                        disabled={processing || formProcessing}
                        variant="primary"
                        className="w-full h-9">
                        Confirm
                    </Button>
                    <Button
                        variant="default"
                        className="w-full mt-2 h-9"
                        onClick={handleClose}>
                        Cancel
                    </Button>
                </div>
            </form>
        </ModalView>
    );
};
